import { useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw, X } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Button,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarCount,
  ToolbarDivider,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
} from '@/components/ui'
import { useDevBoard, useDevQueue } from '../api/queries'
import type { DevBoardItem } from '../api/types'
import { DevItemDrawer } from '../components/DevItemDrawer'
import { StateBadge } from '../components/StateBadge'

type ViewMode = 'board' | 'queue'

function relativeOrDash(iso: string | null): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return '—'
  const diffMs = Date.now() - t
  const days = Math.floor(diffMs / 86_400_000)
  if (days <= 0) {
    const hours = Math.floor(diffMs / 3_600_000)
    return hours <= 0 ? 'just now' : `${hours}h ago`
  }
  return `${days}d ago`
}

export function DevTrackerPage() {
  const [view, setView] = useState<ViewMode>('board')
  const [selectedRef, setSelectedRef] = useState<string | null>(null)
  const board = useDevBoard()
  const queue = useDevQueue()
  const active = view === 'queue' ? queue : board

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [streamFilter, setStreamFilter] = useState('all')
  const [stateFilter, setStateFilter] = useState('all')
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 200)
    return () => clearTimeout(id)
  }, [search])

  const rows = useMemo<DevBoardItem[]>(() => active.data ?? [], [active.data])

  const streamOptions = useMemo(() => {
    return Array.from(new Set(board.data?.map((r) => r.stream) ?? [])).sort()
  }, [board.data])
  const stateOptions = useMemo(() => {
    return Array.from(new Set(board.data?.map((r) => r.state) ?? [])).sort()
  }, [board.data])

  const filtered = useMemo<DevBoardItem[]>(() => {
    const needle = debounced.trim().toLowerCase()
    return rows.filter((r) => {
      if (streamFilter !== 'all' && r.stream !== streamFilter) return false
      if (stateFilter !== 'all' && r.state !== stateFilter) return false
      if (!needle) return true
      return (
        r.ref.toLowerCase().includes(needle) ||
        r.title.toLowerCase().includes(needle) ||
        (r.next_action ?? '').toLowerCase().includes(needle)
      )
    })
  }, [rows, debounced, streamFilter, stateFilter])

  const needsChrisCount = useMemo(
    () => (board.data ?? []).filter((r) => r.state === 'needs_chris').length,
    [board.data],
  )

  const columns = useMemo<ColumnDef<DevBoardItem, unknown>[]>(
    () => [
      {
        id: 'state',
        header: 'State',
        cell: (ctx) => <StateBadge state={ctx.row.original.state} />,
      },
      {
        id: 'ref',
        header: 'Ref',
        accessorKey: 'ref',
        cell: (ctx) => (
          <span className="font-medium text-primary">{ctx.getValue() as string}</span>
        ),
      },
      { id: 'stream', header: 'Stream', accessorKey: 'stream' },
      {
        id: 'title',
        header: 'Title',
        accessorKey: 'title',
        cell: (ctx) => (
          <span className="block max-w-[420px] truncate text-sm text-primary">
            {ctx.getValue() as string}
          </span>
        ),
      },
      {
        id: 'next_action',
        header: 'Next action',
        cell: (ctx) => (
          <span className="block max-w-[320px] truncate text-sm text-secondary">
            {ctx.row.original.next_action ?? '—'}
          </span>
        ),
      },
      {
        id: 'action_owner',
        header: 'Owner',
        cell: (ctx) => ctx.row.original.action_owner ?? '—',
      },
      {
        id: 'last_activity_at',
        header: 'Last activity',
        cell: (ctx) => (
          <span className="text-xs text-secondary">
            {relativeOrDash(ctx.row.original.last_activity_at ?? ctx.row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return
      if (e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
      }
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title="Dev Tracker"
        description="Cross-repo dev work — what's in flight, and what needs a decision."
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard variant="inline" label="Items" value={(board.data?.length ?? 0).toLocaleString()} />
          <MetricCard variant="inline" label="Needs Chris" value={needsChrisCount.toLocaleString()} />
          <MetricCard variant="inline" label="Streams" value={streamOptions.length.toLocaleString()} />
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-w-0 flex-1 flex-col">
        <Toolbar>
          <ViewToggle
            aria-label="View"
            value={view}
            onChange={setView}
            options={[
              { value: 'board', label: 'All items' },
              { value: 'queue', label: 'Queue' },
            ]}
          />
          <SearchInput
            ref={searchRef}
            containerClassName="flex-1 max-w-none"
            placeholder="Search ref, title, next action…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
        </Toolbar>
        <Toolbar>
          <ToolbarLeft>
            <Select
              aria-label="Stream"
              value={streamFilter}
              onChange={(e) => setStreamFilter(e.target.value)}
            >
              <option value="all">All streams</option>
              {streamOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
            <ToolbarDivider />
            <Select
              aria-label="State"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <option value="all">All states</option>
              {stateOptions.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </option>
              ))}
            </Select>
          </ToolbarLeft>
          <ToolbarRight>
            <ToolbarCount>
              {filtered.length.toLocaleString()} of {rows.length.toLocaleString()}
            </ToolbarCount>
            {search || streamFilter !== 'all' || stateFilter !== 'all' ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch('')
                  setStreamFilter('all')
                  setStateFilter('all')
                }}
                title="Clear search + all filter selects"
              >
                <X size={12} /> Reset
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={active.isFetching && !active.isLoading}
              onClick={() => active.refetch()}
            >
              <RefreshCw size={14} />
            </Button>
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 min-h-0 overflow-hidden bg-canvas p-3">
          <DataTable
            error={active.isError ? active.error : undefined}
            onRetry={() => void active.refetch()}
            errorSubject="tracker items"
            data={filtered}
            columns={columns}
            loading={active.isLoading}
            getRowId={(r) => r.ref}
            moduleId="dev-tracker"
            onRowClick={(r) => setSelectedRef(r.ref)}
            maxHeight="100%"
            columnWidths={{
              state: 130,
              ref: 90,
              stream: 110,
              action_owner: 100,
              last_activity_at: 110,
            }}
            emptyState={
              <EmptyState
                variant={rows.length === 0 ? 'no-data' : 'no-match'}
                title={
                  rows.length === 0
                    ? view === 'queue'
                      ? 'Nothing needs a decision right now'
                      : 'No dev items yet'
                    : 'No matches'
                }
                description={
                  rows.length === 0
                    ? view === 'queue'
                      ? "Items land here when they're marked needs_chris or have gone quiet."
                      : 'Items appear here once seeded or reported by a Claude Code session.'
                    : 'Try clearing the search or filters.'
                }
              />
            }
          />
        </div>
      </div>

      <DevItemDrawer itemRef={selectedRef} onClose={() => setSelectedRef(null)} />
    </div>
  )
}
